/**
 * Creature book ("gotta draw them all"): every subject of every world as a collectible card. A card is
 * discovered once the child has drawn that subject (a journal entry / completed lesson); undiscovered
 * cards show as silhouettes. Rarity follows the curriculum level of the subject (L1 common … L4
 * legendary), so harder drawings feel like rarer finds.
 */
import type { Drawing, PathId } from '../content/model';
import type { PathPlan } from '../content/worlds/types';

export type Rarity = 'common' | 'special' | 'rare' | 'legendary';
export const RARITIES: Rarity[] = ['common', 'special', 'rare', 'legendary'];
export const rarityForLevel = (level: number): Rarity => (level >= 4 ? 'legendary' : level === 3 ? 'rare' : level === 2 ? 'special' : 'common');

export interface CreatureSubject {
  drawingId: string;
  world: PathId;
  /** Curriculum level 1..4 (path lessons: their plan level; off-path: from difficulty). */
  level: 1 | 2 | 3 | 4;
  /** On the world's learning path (vs a playground-only lesson). */
  onPath: boolean;
}

/** One record per time a subject was drawn. */
export interface DrawnRecord {
  drawingId: string;
  /** ISO time. */
  at: string;
  journalId?: string;
}

export interface CreatureCard extends CreatureSubject {
  rarity: Rarity;
  discovered: boolean;
  times: number;
  firstAt: string | null;
  lastAt: string | null;
  /** Most recent journal entry of this subject (the child's own photo). */
  latestJournalId: string | null;
  /** Discovered but the card was never opened in the book. */
  fresh: boolean;
}

export interface CreatureWorldPage {
  world: PathId;
  cards: CreatureCard[];
  found: number;
  total: number;
}

export interface CreatureBook {
  pages: CreatureWorldPage[];
  found: number;
  total: number;
  /** A good next subject to discover (lowest level, in the world the child explores most). */
  next: CreatureCard | null;
  /** Count per rarity of discovered cards. */
  byRarity: Record<Rarity, number>;
}

type WorldLike = { id: PathId; plan: Pick<PathPlan, 'levels'>; drawings: Pick<Drawing, 'id' | 'difficulty'>[] };

/** Subjects of every world in path order: the path lessons level by level, then the off-path lessons. */
export function creatureSubjects(worlds: WorldLike[]): CreatureSubject[] {
  const out: CreatureSubject[] = [];
  const seen = new Set<string>();
  for (const w of worlds) {
    w.plan.levels.forEach((lv, i) => {
      for (const id of lv.drawings) {
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ drawingId: id, world: w.id, level: Math.min(4, i + 1) as CreatureSubject['level'], onPath: true });
      }
    });
    for (const d of w.drawings) {
      if (seen.has(d.id) || d.id.startsWith('part-')) continue;
      seen.add(d.id);
      out.push({ drawingId: d.id, world: w.id, level: Math.min(4, Math.max(1, d.difficulty ?? 1)) as CreatureSubject['level'], onPath: false });
    }
  }
  return out;
}

export function creatureBook(subjects: CreatureSubject[], drawn: DrawnRecord[], opened: readonly string[] = []): CreatureBook {
  const by = new Map<string, DrawnRecord[]>();
  for (const r of drawn) {
    const list = by.get(r.drawingId);
    if (list) list.push(r);
    else by.set(r.drawingId, [r]);
  }
  const openedSet = new Set(opened);
  const byRarity: Record<Rarity, number> = { common: 0, special: 0, rare: 0, legendary: 0 };
  const pages = new Map<PathId, CreatureWorldPage>();
  for (const s of subjects) {
    const recs = (by.get(s.drawingId) ?? []).slice().sort((a, b) => a.at.localeCompare(b.at));
    const discovered = recs.length > 0;
    const last = recs[recs.length - 1];
    const card: CreatureCard = {
      ...s,
      rarity: rarityForLevel(s.level),
      discovered,
      times: recs.length,
      firstAt: recs[0]?.at ?? null,
      lastAt: last?.at ?? null,
      latestJournalId: [...recs].reverse().find((r) => r.journalId)?.journalId ?? null,
      fresh: discovered && !openedSet.has(s.drawingId),
    };
    if (discovered) byRarity[card.rarity]++;
    let page = pages.get(s.world);
    if (!page) pages.set(s.world, (page = { world: s.world, cards: [], found: 0, total: 0 }));
    page.cards.push(card);
    page.total++;
    if (discovered) page.found++;
  }
  const list = [...pages.values()];
  const found = list.reduce((n, p) => n + p.found, 0);
  const total = list.reduce((n, p) => n + p.total, 0);
  // Next find: in the world with the most discoveries (ties → path order), the lowest-level undiscovered card.
  const ranked = [...list].sort((a, b) => b.found - a.found);
  let next: CreatureCard | null = null;
  for (const p of ranked) {
    const c = p.cards.filter((x) => !x.discovered).sort((a, b) => a.level - b.level || Number(b.onPath) - Number(a.onPath))[0];
    if (c) {
      next = c;
      break;
    }
  }
  return { pages: list, found, total, next, byRarity };
}
